/**
 * VaultShareTokensSheet — Admin-Overview aller aktiven QR-Code-Share-Tokens.
 * - Live-Restzeit (Sekunden-genaue Anzeige, alle 30s refresh)
 * - Aufrufzaehler (used_count / max_uses)
 * - Sofortiges Zurueckziehen (Revoke) per Klick mit Bestaetigung
 *
 * Mounted als Modal-Sheet aus dem Vault-Header.
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { X, QrCode, Clock, Eye, Trash2, Loader2, ShieldOff, RefreshCw } from "lucide-react";
import { authFetch } from "../../App";

const API = "/api";

function formatRemaining(secs) {
  if (secs <= 0) return "Abgelaufen";
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  if (secs < 86400) {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  return h > 0 ? `${d}T ${h}h` : `${d}T`;
}

export default function VaultShareTokensSheet({ onClose }) {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revokingToken, setRevokingToken] = useState(null);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/vault/share-tokens`);
      if (res.ok) {
        const data = await res.json();
        setTokens(data.tokens || []);
      }
    } catch {
      // silent
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live-Refresh alle 30s + lokaler 1s-Tick fuer Countdown-Anzeige
  useEffect(() => {
    const refresh = setInterval(load, 30_000);
    const local = setInterval(() => setTick(t => t + 1), 1000);
    return () => { clearInterval(refresh); clearInterval(local); };
  }, [load]);

  const revoke = async (tk) => {
    if (!window.confirm(`QR-Code-Token fuer "${tk.entry_title}" jetzt zurueckziehen?\n\nNach Bestaetigung ist der Link sofort ungueltig.`)) return;
    setRevokingToken(tk.token);
    try {
      const res = await authFetch(`${API}/vault/share/${tk.token}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Token zurueckgezogen");
        setTokens(prev => prev.filter(x => x.token !== tk.token));
      } else {
        toast.error("Konnte nicht zurueckgezogen werden");
      }
    } catch {
      toast.error("Netzwerkfehler");
    }
    setRevokingToken(null);
  };

  // Berechnete Live-Restzeit (nutzt expires_at + tick)
  const computeRemaining = (tk) => {
    try {
      const exp = new Date(tk.expires_at).getTime();
      return Math.max(0, Math.floor((exp - Date.now()) / 1000));
    } catch {
      return tk.seconds_left || 0;
    }
  };

  // tick is intentionally referenced so the component re-renders every second
  void tick;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="vault-share-tokens-sheet"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="w-full sm:max-w-2xl bg-[#0f1117] border-t sm:border border-white/10 sm:rounded-2xl max-h-[85vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-amber-500/15 flex items-center justify-center">
              <QrCode className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">Aktive QR-Codes</h2>
              <p className="text-[11px] text-gray-400">
                {loading ? "Laedt..." : `${tokens.length} aktiv${tokens.length === 1 ? "" : "e"}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={load} disabled={loading}
              className="w-9 h-9 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] flex items-center justify-center disabled:opacity-40"
              data-testid="vault-tokens-refresh"
              title="Aktualisieren">
              <RefreshCw className={`w-4 h-4 text-gray-400 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={onClose}
              className="w-9 h-9 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] flex items-center justify-center"
              data-testid="vault-tokens-close">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
            </div>
          ) : tokens.length === 0 ? (
            <div className="text-center py-12">
              <ShieldOff className="w-10 h-10 mx-auto mb-3 text-gray-700" />
              <p className="text-sm text-gray-400">Keine aktiven QR-Codes</p>
              <p className="text-xs text-gray-600 mt-1">Erstelle einen QR-Code aus einem Tresor-Eintrag heraus.</p>
            </div>
          ) : tokens.map((tk) => {
            const remaining = computeRemaining(tk);
            const isExpiringSoon = remaining < 300; // < 5 Min
            const isUsedUp = tk.used_count >= tk.max_uses;
            return (
              <div
                key={tk.token}
                className={`rounded-xl border p-3 ${
                  isExpiringSoon
                    ? "border-red-500/30 bg-red-500/[0.04]"
                    : "border-white/10 bg-white/[0.02]"
                }`}
                data-testid={`vault-token-row-${tk.token.slice(0, 8)}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white truncate" title={tk.entry_title}>
                      {tk.entry_title || "(unbekannt)"}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px]">
                      <span className={`flex items-center gap-1 font-mono font-semibold ${isExpiringSoon ? "text-red-400" : "text-emerald-400"}`}>
                        <Clock className="w-3 h-3" />
                        {formatRemaining(remaining)}
                      </span>
                      <span className={`flex items-center gap-1 ${isUsedUp ? "text-amber-400" : "text-gray-400"}`}>
                        <Eye className="w-3 h-3" />
                        {tk.used_count} / {tk.max_uses} Zugriffe
                      </span>
                      {tk.created_by_name && (
                        <span className="text-gray-500">erstellt von {tk.created_by_name}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => revoke(tk)}
                    disabled={revokingToken === tk.token}
                    className="flex-shrink-0 h-9 px-3 rounded-lg text-[12px] font-bold bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 text-red-300 disabled:opacity-40 flex items-center gap-1.5"
                    data-testid={`vault-token-revoke-${tk.token.slice(0, 8)}`}
                    title="Token sofort zurueckziehen"
                  >
                    {revokingToken === tk.token ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5" />
                    )}
                    Zurueckziehen
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer hint */}
        <div className="px-5 py-3 border-t border-white/10 bg-white/[0.02]">
          <p className="text-[10px] text-gray-500">
            Abgelaufene Tokens werden automatisch 24h nach Ablauf entfernt. Zurueckgezogene Tokens sind sofort ungueltig.
          </p>
        </div>
      </div>
    </div>
  );
}
